import React, { useState, useEffect, useContext, useMemo } from 'react';
import { getDriverTrackStats, getDrivers, DriverTrackStats, getYears, getDriverDetails, getTracks, getTrackDriverStats, TrackDriverStatRow } from '../services/api';
import { Box, Select, MenuItem, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper, SelectChangeEvent, CircularProgress, Button, Menu, Checkbox, ListItemText, IconButton, Autocomplete, TextField, Tabs, Tab, FormControl, InputLabel } from '@mui/material';
import { Track } from '../types/predictions';
import { FilterList, ArrowUpward, ArrowDownward } from '@mui/icons-material';
import {
  DndContext,
  closestCenter,
  PointerSensor,
  useSensor,
  useSensors,
  DragEndEvent,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  useSortable,
  horizontalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { ColorModeContext } from '../App';
import { DriverDetailsModal } from './DriverDetailsModal';
import { DriverDetails } from '../types/predictions';

// Color constants - exactly matching loop data table
const COLORS = {
  max: "#62BF7B", // Green
  mid: "#FDE883", // Yellow
  min: "#F9696D", // Red
};

// Helper function to interpolate between two colors
const interpolateColor = (color1: string, color2: string, factor: number) => {
  const result = color1.match(/\w\w/g)?.map((c, i) => {
    const c1 = parseInt(c, 16);
    const c2 = parseInt(color2.match(/\w\w/g)![i], 16);
    const value = Math.round(c1 + factor * (c2 - c1));
    return value.toString(16).padStart(2, '0');
  });
  return `#${result?.join('')}`;
};

interface Column {
  id: keyof DriverTrackStats;
  label: string;
  defaultVisible?: boolean;
}

/** Track tab: first column is driver name instead of track site. */
interface TrackColumn {
  id: keyof DriverTrackStats | 'Driver';
  label: string;
  defaultVisible?: boolean;
}

/** Sort rows for rankings tables (string compare for name columns). */
function sortRankingRows<T extends Record<string, string | number>>(
  rows: T[],
  col: keyof T,
  dir: 'asc' | 'desc'
): T[] {
  return [...rows].sort((a, b) => {
    const av = a[col];
    const bv = b[col];
    if (typeof av === 'string' || typeof bv === 'string') {
      const as = String(av);
      const bs = String(bv);
      return dir === 'asc' ? as.localeCompare(bs) : bs.localeCompare(as);
    }
    const aValue = Number(av);
    const bValue = Number(bv);
    if (Number.isNaN(aValue) || Number.isNaN(bValue)) return 0;
    return dir === 'asc' ? aValue - bValue : bValue - aValue;
  });
}

const allColumns: Column[] = [
  { id: 'Site', label: 'Track', defaultVisible: true },
  { id: 'DriverRating', label: 'Driver Rating', defaultVisible: true },
  { id: 'St', label: 'Average Start', defaultVisible: true },
  { id: 'Pos', label: 'Average Finish', defaultVisible: true },
  { id: 'Avg', label: 'Average Position', defaultVisible: true },
  { id: 'PctT15Lap', label: '% Top 15 Laps', defaultVisible: true },
  { id: 'PctLed', label: '% Laps Led', defaultVisible: true },
  { id: 'Fastest', label: 'Fastest Laps', defaultVisible: true },
  { id: 'Mid', label: 'Mid-Race Position' },
  { id: 'High', label: 'Highest Position' },
  { id: 'Low', label: 'Lowest Position' },
  { id: 'PassDif', label: 'Pass Differential' },
  { id: 'GfPass', label: 'Green Flag Passes' },
  { id: 'GfPassed', label: 'Green Flag Passed' },
  { id: 'QPass', label: 'Quality Passes' },
  { id: 'PctQPass', label: '% Quality Passes' },
  { id: 'T15Lap', label: 'Top 15 Laps' },
  { id: 'Led', label: 'Laps Led' },
  { id: 'Total', label: 'Total Laps' },
];

const allColumnsByTrack: TrackColumn[] = [
  { id: 'Driver', label: 'Driver', defaultVisible: true },
  ...allColumns.slice(1),
];

// Add color mapping for fields
const colorMapping: Record<string, { type: 'position' | 'probability' | 'rating', invert?: boolean }> = {
  St: { type: 'position'},
  DriverRating: { type: 'rating' },
  Mid: { type: 'position' },
  High: { type: 'position' },
  Low: { type: 'position' },
  Avg: { type: 'position' },
  PassDif: { type: 'rating' },
  GfPass: { type: 'rating' },
  GfPassed: { type: 'rating', invert: true },
  QPass: { type: 'rating' },
  PctQPass: { type: 'probability' },
  Fastest: { type: 'rating' },
  T15Lap: { type: 'rating' },
  PctT15Lap: { type: 'probability' },
  Led: { type: 'rating' },
  PctLed: { type: 'probability' },
  Total: { type: 'rating' },
  Pos: { type: 'position' },
};

// Add SortableHeader component - matching loopdata implementation exactly
const SortableHeader: React.FC<{ 
  column: Column | TrackColumn;
  sortField: keyof DriverTrackStats | 'Driver' | 'TrackRank';
  sortDirection: 'asc' | 'desc';
  onSort: (columnId: keyof DriverTrackStats | 'Driver' | 'TrackRank') => void;
}> = ({ column, sortField, sortDirection, onSort }) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
  } = useSortable({ id: column.id });

  const isNameColumn = column.id === 'Site' || column.id === 'Driver';
  const dragStyle = {
    transform: CSS.Transform.toString(transform),
    transition,
  };
  
  const muiStyle = {
    fontWeight: 'bold',
    whiteSpace: 'pre-line',
    padding: '8px 16px',
    '&:hover': {
      backgroundColor: 'action.hover',
    },
  };

  const handleSortClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    onSort(column.id);
  };

  return (
    <TableCell 
      ref={setNodeRef} 
      sx={muiStyle}
      style={dragStyle}
      {...attributes} 
    >
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          userSelect: 'none',
          width: '100%',
          height: '100%',
        }}
      >
        {/* Main draggable area with column label */}
        <Box
          {...listeners}
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: isNameColumn ? 'flex-start' : 'flex-end',
            flex: 1,
            cursor: 'grab',
            minHeight: '100%',
            '&:active': {
              cursor: 'grabbing',
            },
            '&:hover': {
              backgroundColor: 'rgba(0, 0, 0, 0.05)',
            },
          }}
        >
          {column.label}
        </Box>
        
        {/* Small sort icon area */}
        <Box
          onClick={handleSortClick}
          sx={{
            width: '20px',
            height: '20px',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            borderRadius: '3px',
            ml: 0.5,
            opacity: 0.6,
            '&:hover': {
              opacity: 1,
              backgroundColor: 'rgba(0, 0, 0, 0.08)',
            },
          }}
        >
          {sortField === column.id ? (
            // Show sort direction indicator with subtle green color
            sortDirection === 'asc' ? 
              <ArrowUpward fontSize="small" sx={{ fontSize: '16px', color: '#4caf50', opacity: 0.8 }} /> : 
              <ArrowDownward fontSize="small" sx={{ fontSize: '16px', color: '#4caf50', opacity: 0.8 }} />
          ) : (
            // Show neutral sort icon
            <FilterList fontSize="small" sx={{ fontSize: '16px', color: 'text.secondary' }} />
          )}
        </Box>
      </Box>
    </TableCell>
  );
};

const TrackRankings: React.FC = () => {
  // ?driver= preselects a driver (the Simulator's track-rank pills link here)
  const [selectedDriver, setSelectedDriver] = useState<string[]>(() => {
    const d = new URLSearchParams(window.location.search).get('driver');
    return d ? [d] : [];
  });
  const [selectedYear, setSelectedYear] = useState<number>(new Date().getFullYear() - 2);
  const [drivers, setDrivers] = useState<{ name: string }[]>([]);
  const [years, setYears] = useState<{ year: number }[]>([]);
  const [trackStats, setTrackStats] = useState<DriverTrackStats[]>([]);
  const [visibleColumns, setVisibleColumns] = useState<Column[]>(
    allColumns.filter(col => col.defaultVisible)
  );
  const [sortColumn, setSortColumn] = useState<keyof DriverTrackStats>('DriverRating');
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('desc');
  const [isLoading, setIsLoading] = useState(false);
  const [columnSelectorAnchorEl, setColumnSelectorAnchorEl] = useState<null | HTMLElement>(null);
  const [columnSelectorAnchorElTrack, setColumnSelectorAnchorElTrack] = useState<null | HTMLElement>(null);
  const [isDetailsModalOpen, setIsDetailsModalOpen] = useState(false);
  const [driverDetails, setDriverDetails] = useState<Record<string, DriverDetails[]>>({});
  const [isDetailsLoading, setIsDetailsLoading] = useState(false);
  const [checkedTracks, setCheckedTracks] = useState<Set<string>>(new Set());

  const [rankingTab, setRankingTab] = useState<'driver' | 'track'>('driver');
  const [tracksList, setTracksList] = useState<Track[]>([]);
  const [selectedTrackName, setSelectedTrackName] = useState<string | null>(null);
  const [trackByDriverRaw, setTrackByDriverRaw] = useState<TrackDriverStatRow[]>([]);
  const [visibleColumnsTrack, setVisibleColumnsTrack] = useState<TrackColumn[]>(
    allColumnsByTrack.filter(col => col.defaultVisible)
  );
  const [rankStatTrack, setRankStatTrack] = useState<keyof Omit<DriverTrackStats, 'Site'>>('Pos');
  const [sortColumnTrack, setSortColumnTrack] = useState<keyof TrackDriverStatRow>('TrackRank');
  const [sortDirectionTrack, setSortDirectionTrack] = useState<'asc' | 'desc'>('asc');
  const [isLoadingTrackView, setIsLoadingTrackView] = useState(false);
  const [checkedDrivers, setCheckedDrivers] = useState<Set<string>>(new Set());

  const sensors = useSensors(useSensor(PointerSensor));

  const sortedTrackDriverRows = useMemo(
    () => sortRankingRows(trackByDriverRaw, sortColumnTrack, sortDirectionTrack),
    [trackByDriverRaw, sortColumnTrack, sortDirectionTrack]
  );

  const { mode } = useContext(ColorModeContext);

  useEffect(() => {
    const fetchInitialData = async () => {
      try {
        const [driversData, yearsData, tracksData] = await Promise.all([
          getDrivers(),
          getYears(),
          getTracks(),
        ]);
        setDrivers(driversData);
        setYears(yearsData);
        setTracksList(tracksData);
      } catch (error) {
        console.error('Error fetching initial data:', error);
      }
    };
    fetchInitialData();
  }, []);

  useEffect(() => {
    const fetchTrackStats = async () => {
      if (selectedDriver.length > 0) {
        setIsLoading(true);
        try {
          const stats = await getDriverTrackStats({
            driver: selectedDriver[0],
            year: selectedYear,
          });
          setTrackStats(stats);
        } catch (error) {
          console.error('Error fetching track stats:', error);
        } finally {
          setIsLoading(false);
        }
      }
    };

    fetchTrackStats();
  }, [selectedDriver, selectedYear]);

  useEffect(() => {
    const fetchTrackDriverView = async () => {
      if (rankingTab !== 'track' || !selectedTrackName) {
        setTrackByDriverRaw([]);
        return;
      }
      setIsLoadingTrackView(true);
      try {
        const stats = await getTrackDriverStats({
          track: selectedTrackName,
          year: selectedYear,
          rankStat: rankStatTrack,
        });
        setTrackByDriverRaw(stats);
      } catch (error) {
        console.error('Error fetching track driver stats:', error);
        setTrackByDriverRaw([]);
      } finally {
        setIsLoadingTrackView(false);
      }
    };

    fetchTrackDriverView();
  }, [rankingTab, selectedTrackName, selectedYear, rankStatTrack]);

  // Sort trackStats when sortColumn or sortDirection changes
  useEffect(() => {
    if (trackStats.length > 0) {
      const sortedStats = [...trackStats].sort((a, b) => {
        const aValue = Number(a[sortColumn]);
        const bValue = Number(b[sortColumn]);
        return sortDirection === 'asc' ? aValue - bValue : bValue - aValue;
      });
      setTrackStats(sortedStats);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sortColumn, sortDirection]);

  const handleTrackCheckboxChange = (track: string) => {
    setCheckedTracks(prev => {
      const newChecked = new Set(prev);
      if (newChecked.has(track)) {
        newChecked.delete(track);
      } else {
        newChecked.add(track);
      }
      return newChecked;
    });
  };

  const handleDriverCheckboxChange = (driver: string) => {
    setCheckedDrivers(prev => {
      const next = new Set(prev);
      if (next.has(driver)) {
        next.delete(driver);
      } else {
        next.add(driver);
      }
      return next;
    });
  };

  const handleRankStatTrackChange = (event: SelectChangeEvent) => {
    const v = event.target.value as keyof Omit<DriverTrackStats, 'Site'>;
    setRankStatTrack(v);
    setSortColumnTrack('TrackRank');
    setSortDirectionTrack('asc');
  };

  const handleSortChangeTrack = (event: SelectChangeEvent) => {
    const newSortColumn = event.target.value as keyof TrackDriverStatRow;
    setSortColumnTrack(newSortColumn);
  };

  const handleSortDirectionChangeTrack = () => {
    setSortDirectionTrack(prev => (prev === 'asc' ? 'desc' : 'asc'));
  };

  const handleColumnSortTrack = (columnId: keyof DriverTrackStats | 'Driver' | 'TrackRank') => {
    if (columnId === 'Site') return;
    const col = columnId as keyof TrackDriverStatRow;
    if (sortColumnTrack === col) {
      setSortDirectionTrack(prev => (prev === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortColumnTrack(col);
      setSortDirectionTrack(columnId === 'Driver' || columnId === 'TrackRank' ? 'asc' : 'desc');
    }
  };

  const handleColumnToggleTrack = (columnId: keyof TrackDriverStatRow) => {
    setVisibleColumnsTrack(prevColumns => {
      const isCurrentlyVisible = prevColumns.some(col => col.id === columnId);
      if (isCurrentlyVisible) {
        if (columnId === 'Driver' || prevColumns.length <= 2) {
          return prevColumns;
        }
        return prevColumns.filter(col => col.id !== columnId);
      }
      const columnToAdd = allColumnsByTrack.find(col => col.id === columnId)!;
      return [...prevColumns, columnToAdd];
    });
  };

  const handleDriverRowClickTrack = async (driverName: string) => {
    if (!selectedTrackName) return;

    setIsDetailsLoading(true);
    setIsDetailsModalOpen(true);

    try {
      const details = await getDriverDetails([driverName], [selectedTrackName], selectedYear);
      setDriverDetails({ [driverName]: details });
    } catch (error) {
      console.error('Error fetching driver details:', error);
    } finally {
      setIsDetailsLoading(false);
    }
  };

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    
    if (over && active.id !== over.id) {
      setVisibleColumns((prevColumns) => {
        const oldIndex = prevColumns.findIndex((col) => col.id === active.id);
        const newIndex = prevColumns.findIndex((col) => col.id === over.id);
        return arrayMove(prevColumns, oldIndex, newIndex);
      });
    }
  };

  const handleDragEndTrack = (event: DragEndEvent) => {
    const { active, over } = event;

    if (over && active.id !== over.id) {
      setVisibleColumnsTrack((prevColumns) => {
        const oldIndex = prevColumns.findIndex((col) => col.id === active.id);
        const newIndex = prevColumns.findIndex((col) => col.id === over.id);
        return arrayMove(prevColumns, oldIndex, newIndex);
      });
    }
  };

  const handleSortChange = (event: SelectChangeEvent) => {
    const newSortColumn = event.target.value as keyof DriverTrackStats;
    setSortColumn(newSortColumn);
    const sortedStats = [...trackStats].sort((a, b) => {
      const aValue = Number(a[newSortColumn]);
      const bValue = Number(b[newSortColumn]);
      return sortDirection === 'asc' ? aValue - bValue : bValue - aValue;
    });
    setTrackStats(sortedStats);
  };

  const handleSortDirectionChange = () => {
    setSortDirection(prev => {
      const newDirection = prev === 'asc' ? 'desc' : 'asc';
      const sortedStats = [...trackStats].sort((a, b) => {
        const aValue = Number(a[sortColumn]);
        const bValue = Number(b[sortColumn]);
        return newDirection === 'asc' ? aValue - bValue : bValue - aValue;
      });
      setTrackStats(sortedStats);
      return newDirection;
    });
  };

  const handleColumnSort = (columnId: keyof DriverTrackStats | 'Driver' | 'TrackRank') => {
    if (columnId === 'Driver' || columnId === 'TrackRank') return;
    if (sortColumn === columnId) {
      // If clicking the same column, toggle direction
      setSortDirection(prev => prev === 'asc' ? 'desc' : 'asc');
    } else {
      // If clicking a different column, set it as the new sort field and default to desc for most columns
      setSortColumn(columnId);
      // Default to descending for most columns, ascending for Site name
      setSortDirection(columnId === 'Site' ? 'asc' : 'desc');
    }
  };

  const handleColumnToggle = (columnId: keyof DriverTrackStats) => {
    setVisibleColumns(prevColumns => {
      const isCurrentlyVisible = prevColumns.some(col => col.id === columnId);
      if (isCurrentlyVisible) {
        // Don't allow removing the Track column or having no columns
        if (columnId === 'Site' || prevColumns.length <= 2) {
          return prevColumns;
        }
        return prevColumns.filter(col => col.id !== columnId);
      } else {
        const columnToAdd = allColumns.find(col => col.id === columnId)!;
        return [...prevColumns, columnToAdd];
      }
    });
  };

  const handleColumnSelectorClick = (event: React.MouseEvent<HTMLElement>) => {
    setColumnSelectorAnchorEl(event.currentTarget);
  };

  const handleColumnSelectorClose = () => {
    setColumnSelectorAnchorEl(null);
  };

  const getCellColor = (value: number, field: string) => {
    const mapping = colorMapping[field];
    if (!mapping || field === 'Site' || field === 'Driver') return undefined;

    let normalizedValue: number;
    switch (mapping.type) {
      case 'position':
        // For positions: 1 (green) to 40 (red)
        normalizedValue = Math.min(Math.max(value, 1), 40);
        normalizedValue = mapping.invert ? 
          (normalizedValue - 1) / 39 : // 39 is the range (40 - 1)
          1 - ((normalizedValue - 1) / 39);
        break;
      case 'probability':
        // For probabilities: 0 (red) to 100 (green)
        normalizedValue = Math.min(Math.max(value * 100, 0), 100) / 100;
        break;
      case 'rating':
        // For ratings: scale based on field
        const min = 0;
        const max = value > 100 ? value * 1.1 : 100;
        normalizedValue = Math.max(0, Math.min(1, (value - min) / (max - min)));
        normalizedValue = mapping.invert ? 1 - normalizedValue : normalizedValue;
        break;
      default:
        return undefined;
    }

    if (normalizedValue <= 0.5) {
      return interpolateColor(COLORS.min, COLORS.mid, normalizedValue * 2);
    } else {
      return interpolateColor(COLORS.mid, COLORS.max, (normalizedValue - 0.5) * 2);
    }
  };

  const handleTrackRowClick = async (track: string) => {
    if (selectedDriver.length === 0) return;
    
    setIsDetailsLoading(true);
    setIsDetailsModalOpen(true);

    try {
      const details = await getDriverDetails(
        selectedDriver,
        [track],
        selectedYear
      );
      setDriverDetails({ [selectedDriver[0]]: details });
    } catch (error) {
      console.error('Error fetching driver details:', error);
    } finally {
      setIsDetailsLoading(false);
    }
  };

  return (
    <Box sx={{ p: 3 }}>
      <Tabs
        value={rankingTab}
        onChange={(_, v) => setRankingTab(v as 'driver' | 'track')}
        sx={{ mb: 2, borderBottom: 1, borderColor: 'divider' }}
      >
        <Tab label="By Driver" value="driver" />
        <Tab label="By Track" value="track" />
      </Tabs>

      {rankingTab === 'driver' && (
      <>
      <Box sx={{ mb: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
          <Autocomplete
            value={selectedDriver[0] || null}
            onChange={(_, newValue) => {
              setSelectedDriver(newValue ? [newValue] : []);
            }}
            options={drivers.map(d => d.name)}
            sx={{ 
              minWidth: 200,
              '& .MuiAutocomplete-clearIndicator, & .MuiAutocomplete-popupIndicator': {
                color: (theme) => theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.7)' : 'rgba(0, 0, 0, 0.7)',
                '&:hover': {
                  color: (theme) => theme.palette.mode === 'dark' ? '#fff' : '#000',
                },
              },
            }}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Select a Driver"
                placeholder="Search drivers..."
                sx={{
                  '& .MuiOutlinedInput-root': {
                    '& fieldset': {
                      borderColor: (theme) => theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.23)' : 'rgba(0, 0, 0, 0.23)',
                    },
                    '&:hover fieldset': {
                      borderColor: (theme) => theme.palette.mode === 'dark' ? '#fff' : '#000',
                    },
                    '&.Mui-focused fieldset': {
                      borderColor: (theme) => theme.palette.mode === 'dark' ? '#fff' : '#000',
                    },
                  },
                  '& .MuiInputLabel-root.Mui-focused': {
                    color: (theme) => theme.palette.mode === 'dark' ? '#fff' : '#000',
                  },
                }}
              />
            )}
          />

          <Select
            value={selectedYear}
            onChange={(e) => setSelectedYear(Number(e.target.value))}
            sx={{ 
              minWidth: 120,
              '& .MuiOutlinedInput-notchedOutline': {
                borderColor: (theme) => theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.23)' : 'rgba(0, 0, 0, 0.23)',
              },
              '&:hover .MuiOutlinedInput-notchedOutline': {
                borderColor: (theme) => theme.palette.mode === 'dark' ? '#fff' : '#000',
              },
              '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                borderColor: (theme) => theme.palette.mode === 'dark' ? '#fff' : '#000',
              },
              '& .MuiSelect-icon': {
                color: (theme) => theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.7)' : 'rgba(0, 0, 0, 0.7)',
              },
              '&:hover .MuiSelect-icon': {
                color: (theme) => theme.palette.mode === 'dark' ? '#fff' : '#000',
              },
            }}
            MenuProps={{
              PaperProps: {
                sx: {
                  backgroundColor: 'background.paper',
                  '& .MuiMenuItem-root': {
                    '&:hover': {
                      backgroundColor: (theme) => 
                        theme.palette.mode === 'dark' 
                          ? 'rgba(255, 255, 255, 0.08)'
                          : 'rgba(0, 0, 0, 0.04)',
                    },
                    '&.Mui-selected': {
                      backgroundColor: (theme) => 
                        theme.palette.mode === 'dark' 
                          ? 'rgba(255, 255, 255, 0.16)'
                          : 'rgba(0, 0, 0, 0.08)',
                      '&:hover': {
                        backgroundColor: (theme) => 
                          theme.palette.mode === 'dark' 
                            ? 'rgba(255, 255, 255, 0.24)'
                            : 'rgba(0, 0, 0, 0.12)',
                      },
                    },
                  },
                },
              },
            }}
          >
            {years.map((year) => (
              <MenuItem key={year.year} value={year.year}>
                {year.year}
              </MenuItem>
            ))}
          </Select>

          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Select
              value={sortColumn}
              onChange={handleSortChange}
              sx={{ 
                minWidth: 200,
                '& .MuiOutlinedInput-notchedOutline': {
                  borderColor: (theme) => theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.23)' : 'rgba(0, 0, 0, 0.23)',
                },
                '&:hover .MuiOutlinedInput-notchedOutline': {
                  borderColor: (theme) => theme.palette.mode === 'dark' ? '#fff' : '#000',
                },
                '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                  borderColor: (theme) => theme.palette.mode === 'dark' ? '#fff' : '#000',
                },
                '& .MuiSelect-icon': {
                  color: (theme) => theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.7)' : 'rgba(0, 0, 0, 0.7)',
                },
                '&:hover .MuiSelect-icon': {
                  color: (theme) => theme.palette.mode === 'dark' ? '#fff' : '#000',
                },
              }}
              MenuProps={{
                PaperProps: {
                  sx: {
                    backgroundColor: 'background.paper',
                    '& .MuiMenuItem-root': {
                      '&:hover': {
                        backgroundColor: (theme) => 
                          theme.palette.mode === 'dark' 
                            ? 'rgba(255, 255, 255, 0.08)'
                            : 'rgba(0, 0, 0, 0.04)',
                      },
                      '&.Mui-selected': {
                        backgroundColor: (theme) => 
                          theme.palette.mode === 'dark' 
                            ? 'rgba(255, 255, 255, 0.16)'
                            : 'rgba(0, 0, 0, 0.08)',
                        '&:hover': {
                          backgroundColor: (theme) => 
                            theme.palette.mode === 'dark' 
                              ? 'rgba(255, 255, 255, 0.24)'
                              : 'rgba(0, 0, 0, 0.12)',
                        },
                      },
                    },
                  },
                },
              }}
            >
              {visibleColumns.map((column) => (
                <MenuItem key={column.id} value={column.id}>
                  Sort by {column.label}
                </MenuItem>
              ))}
            </Select>
            <IconButton 
              onClick={handleSortDirectionChange} 
              size="small"
              sx={{
                color: (theme) => theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.7)' : 'rgba(0, 0, 0, 0.7)',
                '&:hover': {
                  color: (theme) => theme.palette.mode === 'dark' ? '#fff' : '#000',
                },
              }}
            >
              {sortDirection === 'asc' ? <ArrowUpward /> : <ArrowDownward />}
            </IconButton>
          </Box>
        </Box>
        <Button
          onClick={handleColumnSelectorClick}
          variant="outlined"
          startIcon={<FilterList />}
          sx={{
            color: (theme) => theme.palette.mode === 'dark' ? '#fff' : '#000',
            borderColor: (theme) => theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.23)' : 'rgba(0, 0, 0, 0.23)',
            '&:hover': {
              backgroundColor: (theme) => 
                theme.palette.mode === 'dark' 
                  ? 'rgba(255, 255, 255, 0.08)'
                  : 'rgba(0, 0, 0, 0.04)',
              borderColor: (theme) => theme.palette.mode === 'dark' ? '#fff' : '#000',
            },
          }}
        >
          Columns
        </Button>
        <Menu
          anchorEl={columnSelectorAnchorEl}
          open={Boolean(columnSelectorAnchorEl)}
          onClose={handleColumnSelectorClose}
          PaperProps={{
            sx: {
              maxHeight: 300,
              width: 250,
              backgroundColor: 'background.paper',
              '& .MuiMenuItem-root': {
                '&:hover': {
                  backgroundColor: (theme) => 
                    theme.palette.mode === 'dark' 
                      ? 'rgba(255, 255, 255, 0.08)'
                      : 'rgba(0, 0, 0, 0.04)',
                },
              },
            },
          }}
        >
          {allColumns.map((column) => (
            <MenuItem
              key={column.id}
              onClick={() => handleColumnToggle(column.id)}
              dense
            >
              <Checkbox
                checked={visibleColumns.some(col => col.id === column.id)}
                disabled={column.id === 'Site'}
              />
              <ListItemText primary={column.label} />
            </MenuItem>
          ))}
        </Menu>
      </Box>

      <DndContext
        sensors={sensors}
        collisionDetection={closestCenter}
        onDragEnd={handleDragEnd}
      >
        <TableContainer component={Paper} sx={{ 
          backgroundColor: mode === 'dark' ? 'rgba(255, 255, 255, 0.05)' : undefined,
        }}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell 
                  sx={{ 
                    fontWeight: 'bold',
                    width: '60px',
                    backgroundColor: mode === 'dark' ? 'rgba(255, 255, 255, 0.08)' : 'background.paper',
                    position: 'sticky',
                    left: 0,
                    zIndex: 2,
                    borderRight: `1px solid ${mode === 'dark' ? 'rgba(255, 255, 255, 0.12)' : 'rgba(224, 224, 224, 1)'}`,
                  }}
                >
                  Select
                </TableCell>
                <TableCell 
                  sx={{ 
                    fontWeight: 'bold',
                    width: '60px',
                    backgroundColor: mode === 'dark' ? 'rgba(255, 255, 255, 0.08)' : 'background.paper',
                    position: 'sticky',
                    left: '60px',
                    zIndex: 2,
                    borderRight: `1px solid ${mode === 'dark' ? 'rgba(255, 255, 255, 0.12)' : 'rgba(224, 224, 224, 1)'}`,
                  }}
                >
                  Rank
                </TableCell>
                <SortableContext
                  items={visibleColumns.map(col => col.id)}
                  strategy={horizontalListSortingStrategy}
                >
                  {visibleColumns.map((column) => (
                    <SortableHeader 
                      key={column.id} 
                      column={column}
                      sortField={sortColumn}
                      sortDirection={sortDirection}
                      onSort={handleColumnSort}
                    />
                  ))}
                </SortableContext>
              </TableRow>
            </TableHead>
            <TableBody>
              {isLoading ? (
                <TableRow>
                  <TableCell colSpan={visibleColumns.length + 2} align="center">
                    <CircularProgress />
                  </TableCell>
                </TableRow>
              ) : (
                trackStats.map((row, rowIndex) => {
                  const isChecked = checkedTracks.has(row.Site);
                  const hasCheckedTracks = checkedTracks.size > 0;
                  const shouldGrayOut = hasCheckedTracks && !isChecked;
                  
                  return (
                    <TableRow
                      key={row.Site}
                      hover
                      onClick={() => handleTrackRowClick(row.Site)}
                      sx={{ 
                        cursor: 'pointer',
                        opacity: shouldGrayOut ? 0.4 : 1,
                        '&:hover': {
                          backgroundColor: 'rgba(0, 0, 0, 0.04)',
                        },
                      }}
                    >
                      <TableCell
                        sx={{
                          backgroundColor: mode === 'dark' ? 'rgba(255, 255, 255, 0.08)' : 'background.paper',
                          position: 'sticky',
                          left: 0,
                          zIndex: 1,
                          borderRight: `1px solid ${mode === 'dark' ? 'rgba(255, 255, 255, 0.12)' : 'rgba(224, 224, 224, 1)'}`,
                          py: 0.25,
                          px: 1,
                        }}
                        onClick={(e) => e.stopPropagation()}
                      >
                        <Checkbox
                          checked={isChecked}
                          onChange={() => handleTrackCheckboxChange(row.Site)}
                          size="small"
                        />
                      </TableCell>
                      <TableCell
                        sx={{
                          backgroundColor: mode === 'dark' ? 'rgba(255, 255, 255, 0.08)' : 'background.paper',
                          position: 'sticky',
                          left: '60px',
                          zIndex: 1,
                          borderRight: `1px solid ${mode === 'dark' ? 'rgba(255, 255, 255, 0.12)' : 'rgba(224, 224, 224, 1)'}`,
                          fontWeight: 'medium',
                          py: 0.25,
                          px: 1,
                        }}
                      >
                        {rowIndex + 1}
                      </TableCell>
                                            {visibleColumns.map((column) => {
                      const value = row[column.id];
                      return (
                        <TableCell
                          key={column.id}
                          align={column.id === 'Site' ? 'left' : 'right'}
                          sx={{
                            backgroundColor: getCellColor(Number(value), column.id),
                            color: column.id !== 'Site' ? (mode === 'dark' ? '#000000' : '#000000') : undefined,
                            fontWeight: 'medium',
                            transition: 'all 0.2s',
                            py: 0.25,
                            px: 1,
                          }}
                        >
                          {column.id === 'Site' ? value :
                           column.id === 'PctQPass' || column.id === 'PctT15Lap' || column.id === 'PctLed' 
                            ? `${Number(value).toFixed(1)}%` :
                           column.id === 'DriverRating' || column.id === 'Avg' || column.id === 'Pos' || column.id === 'St'
                            ? Number(value).toFixed(1) :
                           Math.round(Number(value))}
                        </TableCell>
                      );
                    })}
                  </TableRow>
                );
                })
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </DndContext>
      </>
      )}

      {rankingTab === 'track' && (
      <>
      <Box sx={{ mb: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
          <Autocomplete
            value={selectedTrackName}
            onChange={(_, newValue) => {
              setSelectedTrackName(newValue);
              setCheckedDrivers(new Set());
            }}
            options={tracksList.map(t => t.name)}
            sx={{ 
              minWidth: 220,
              '& .MuiAutocomplete-clearIndicator, & .MuiAutocomplete-popupIndicator': {
                color: (theme) => theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.7)' : 'rgba(0, 0, 0, 0.7)',
                '&:hover': {
                  color: (theme) => theme.palette.mode === 'dark' ? '#fff' : '#000',
                },
              },
            }}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Select a Track"
                placeholder="Search tracks..."
                sx={{
                  '& .MuiOutlinedInput-root': {
                    '& fieldset': {
                      borderColor: (theme) => theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.23)' : 'rgba(0, 0, 0, 0.23)',
                    },
                    '&:hover fieldset': {
                      borderColor: (theme) => theme.palette.mode === 'dark' ? '#fff' : '#000',
                    },
                    '&.Mui-focused fieldset': {
                      borderColor: (theme) => theme.palette.mode === 'dark' ? '#fff' : '#000',
                    },
                  },
                  '& .MuiInputLabel-root.Mui-focused': {
                    color: (theme) => theme.palette.mode === 'dark' ? '#fff' : '#000',
                  },
                }}
              />
            )}
          />

          <Select
            value={selectedYear}
            onChange={(e) => setSelectedYear(Number(e.target.value))}
            sx={{ 
              minWidth: 120,
              '& .MuiOutlinedInput-notchedOutline': {
                borderColor: (theme) => theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.23)' : 'rgba(0, 0, 0, 0.23)',
              },
              '&:hover .MuiOutlinedInput-notchedOutline': {
                borderColor: (theme) => theme.palette.mode === 'dark' ? '#fff' : '#000',
              },
              '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                borderColor: (theme) => theme.palette.mode === 'dark' ? '#fff' : '#000',
              },
              '& .MuiSelect-icon': {
                color: (theme) => theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.7)' : 'rgba(0, 0, 0, 0.7)',
              },
              '&:hover .MuiSelect-icon': {
                color: (theme) => theme.palette.mode === 'dark' ? '#fff' : '#000',
              },
            }}
            MenuProps={{
              PaperProps: {
                sx: {
                  backgroundColor: 'background.paper',
                  '& .MuiMenuItem-root': {
                    '&:hover': {
                      backgroundColor: (theme) => 
                        theme.palette.mode === 'dark' 
                          ? 'rgba(255, 255, 255, 0.08)'
                          : 'rgba(0, 0, 0, 0.04)',
                    },
                    '&.Mui-selected': {
                      backgroundColor: (theme) => 
                        theme.palette.mode === 'dark' 
                          ? 'rgba(255, 255, 255, 0.16)'
                          : 'rgba(0, 0, 0, 0.08)',
                      '&:hover': {
                        backgroundColor: (theme) => 
                          theme.palette.mode === 'dark' 
                            ? 'rgba(255, 255, 255, 0.24)'
                            : 'rgba(0, 0, 0, 0.12)',
                      },
                    },
                  },
                },
              },
            }}
          >
            {years.map((year) => (
              <MenuItem key={year.year} value={year.year}>
                {year.year}
              </MenuItem>
            ))}
          </Select>

          <FormControl sx={{ minWidth: 240 }} size="small">
            <InputLabel id="track-rank-stat-label">Statistic</InputLabel>
            <Select
              labelId="track-rank-stat-label"
              label="Statistic"
              value={rankStatTrack}
              onChange={handleRankStatTrackChange}
              sx={{
                '& .MuiOutlinedInput-notchedOutline': {
                  borderColor: (theme) => theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.23)' : 'rgba(0, 0, 0, 0.23)',
                },
                '&:hover .MuiOutlinedInput-notchedOutline': {
                  borderColor: (theme) => theme.palette.mode === 'dark' ? '#fff' : '#000',
                },
                '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                  borderColor: (theme) => theme.palette.mode === 'dark' ? '#fff' : '#000',
                },
                '& .MuiSelect-icon': {
                  color: (theme) => theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.7)' : 'rgba(0, 0, 0, 0.7)',
                },
              }}
              MenuProps={{
                PaperProps: {
                  sx: {
                    backgroundColor: 'background.paper',
                    '& .MuiMenuItem-root': {
                      '&:hover': {
                        backgroundColor: (theme) =>
                          theme.palette.mode === 'dark'
                            ? 'rgba(255, 255, 255, 0.08)'
                            : 'rgba(0, 0, 0, 0.04)',
                      },
                      '&.Mui-selected': {
                        backgroundColor: (theme) =>
                          theme.palette.mode === 'dark'
                            ? 'rgba(255, 255, 255, 0.16)'
                            : 'rgba(0, 0, 0, 0.08)',
                        '&:hover': {
                          backgroundColor: (theme) =>
                            theme.palette.mode === 'dark'
                              ? 'rgba(255, 255, 255, 0.24)'
                              : 'rgba(0, 0, 0, 0.12)',
                        },
                      },
                    },
                  },
                },
              }}
            >
              {allColumns.slice(1).map((col) => (
                <MenuItem key={col.id} value={col.id}>
                  {col.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Select
              value={sortColumnTrack}
              onChange={handleSortChangeTrack}
              sx={{ 
                minWidth: 200,
                '& .MuiOutlinedInput-notchedOutline': {
                  borderColor: (theme) => theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.23)' : 'rgba(0, 0, 0, 0.23)',
                },
                '&:hover .MuiOutlinedInput-notchedOutline': {
                  borderColor: (theme) => theme.palette.mode === 'dark' ? '#fff' : '#000',
                },
                '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                  borderColor: (theme) => theme.palette.mode === 'dark' ? '#fff' : '#000',
                },
                '& .MuiSelect-icon': {
                  color: (theme) => theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.7)' : 'rgba(0, 0, 0, 0.7)',
                },
                '&:hover .MuiSelect-icon': {
                  color: (theme) => theme.palette.mode === 'dark' ? '#fff' : '#000',
                },
              }}
              MenuProps={{
                PaperProps: {
                  sx: {
                    backgroundColor: 'background.paper',
                    '& .MuiMenuItem-root': {
                      '&:hover': {
                        backgroundColor: (theme) => 
                          theme.palette.mode === 'dark' 
                            ? 'rgba(255, 255, 255, 0.08)'
                            : 'rgba(0, 0, 0, 0.04)',
                      },
                      '&.Mui-selected': {
                        backgroundColor: (theme) => 
                          theme.palette.mode === 'dark' 
                            ? 'rgba(255, 255, 255, 0.16)'
                            : 'rgba(0, 0, 0, 0.08)',
                        '&:hover': {
                          backgroundColor: (theme) => 
                            theme.palette.mode === 'dark' 
                              ? 'rgba(255, 255, 255, 0.24)'
                              : 'rgba(0, 0, 0, 0.12)',
                        },
                      },
                    },
                  },
                },
              }}
            >
              <MenuItem value="TrackRank">Track rank (for statistic)</MenuItem>
              {visibleColumnsTrack.map((column) => (
                <MenuItem key={column.id} value={column.id}>
                  Sort by {column.label}
                </MenuItem>
              ))}
            </Select>
            <IconButton 
              onClick={handleSortDirectionChangeTrack} 
              size="small"
              sx={{
                color: (theme) => theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.7)' : 'rgba(0, 0, 0, 0.7)',
                '&:hover': {
                  color: (theme) => theme.palette.mode === 'dark' ? '#fff' : '#000',
                },
              }}
            >
              {sortDirectionTrack === 'asc' ? <ArrowUpward /> : <ArrowDownward />}
            </IconButton>
          </Box>
        </Box>
        <Button
          onClick={(e) => setColumnSelectorAnchorElTrack(e.currentTarget)}
          variant="outlined"
          startIcon={<FilterList />}
          sx={{
            color: (theme) => theme.palette.mode === 'dark' ? '#fff' : '#000',
            borderColor: (theme) => theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.23)' : 'rgba(0, 0, 0, 0.23)',
            '&:hover': {
              backgroundColor: (theme) => 
                theme.palette.mode === 'dark' 
                  ? 'rgba(255, 255, 255, 0.08)'
                  : 'rgba(0, 0, 0, 0.04)',
              borderColor: (theme) => theme.palette.mode === 'dark' ? '#fff' : '#000',
            },
          }}
        >
          Columns
        </Button>
        <Menu
          anchorEl={columnSelectorAnchorElTrack}
          open={Boolean(columnSelectorAnchorElTrack)}
          onClose={() => setColumnSelectorAnchorElTrack(null)}
          PaperProps={{
            sx: {
              maxHeight: 300,
              width: 250,
              backgroundColor: 'background.paper',
              '& .MuiMenuItem-root': {
                '&:hover': {
                  backgroundColor: (theme) => 
                    theme.palette.mode === 'dark' 
                      ? 'rgba(255, 255, 255, 0.08)'
                      : 'rgba(0, 0, 0, 0.04)',
                },
              },
            },
          }}
        >
          {allColumnsByTrack.map((column) => (
            <MenuItem
              key={column.id}
              onClick={() => handleColumnToggleTrack(column.id as keyof TrackDriverStatRow)}
              dense
            >
              <Checkbox
                checked={visibleColumnsTrack.some(col => col.id === column.id)}
                disabled={column.id === 'Driver'}
              />
              <ListItemText primary={column.label} />
            </MenuItem>
          ))}
        </Menu>
      </Box>

      <DndContext
        sensors={sensors}
        collisionDetection={closestCenter}
        onDragEnd={handleDragEndTrack}
      >
        <TableContainer component={Paper} sx={{ 
          backgroundColor: mode === 'dark' ? 'rgba(255, 255, 255, 0.05)' : undefined,
        }}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell 
                  sx={{ 
                    fontWeight: 'bold',
                    width: '60px',
                    backgroundColor: mode === 'dark' ? 'rgba(255, 255, 255, 0.08)' : 'background.paper',
                    position: 'sticky',
                    left: 0,
                    zIndex: 2,
                    borderRight: `1px solid ${mode === 'dark' ? 'rgba(255, 255, 255, 0.12)' : 'rgba(224, 224, 224, 1)'}`,
                  }}
                >
                  Select
                </TableCell>
                <TableCell 
                  sx={{ 
                    fontWeight: 'bold',
                    width: '60px',
                    backgroundColor: mode === 'dark' ? 'rgba(255, 255, 255, 0.08)' : 'background.paper',
                    position: 'sticky',
                    left: '60px',
                    zIndex: 2,
                    borderRight: `1px solid ${mode === 'dark' ? 'rgba(255, 255, 255, 0.12)' : 'rgba(224, 224, 224, 1)'}`,
                  }}
                >
                  Track rank
                </TableCell>
                <SortableContext
                  items={visibleColumnsTrack.map(col => col.id)}
                  strategy={horizontalListSortingStrategy}
                >
                  {visibleColumnsTrack.map((column) => (
                    <SortableHeader 
                      key={column.id} 
                      column={column}
                      sortField={sortColumnTrack}
                      sortDirection={sortDirectionTrack}
                      onSort={handleColumnSortTrack}
                    />
                  ))}
                </SortableContext>
              </TableRow>
            </TableHead>
            <TableBody>
              {!selectedTrackName ? (
                <TableRow>
                  <TableCell colSpan={visibleColumnsTrack.length + 2} align="center">
                    Select a track to see how each driver ranks this track vs their other tracks (for the chosen statistic).
                  </TableCell>
                </TableRow>
              ) : isLoadingTrackView ? (
                <TableRow>
                  <TableCell colSpan={visibleColumnsTrack.length + 2} align="center">
                    <CircularProgress />
                  </TableCell>
                </TableRow>
              ) : (
                sortedTrackDriverRows.map((row, rowIndex) => {
                  const isChecked = checkedDrivers.has(row.Driver);
                  const hasChecked = checkedDrivers.size > 0;
                  const shouldGrayOut = hasChecked && !isChecked;
                  
                  return (
                    <TableRow
                      key={row.Driver}
                      hover
                      onClick={() => handleDriverRowClickTrack(row.Driver)}
                      sx={{ 
                        cursor: 'pointer',
                        opacity: shouldGrayOut ? 0.4 : 1,
                        '&:hover': {
                          backgroundColor: 'rgba(0, 0, 0, 0.04)',
                        },
                      }}
                    >
                      <TableCell
                        sx={{
                          backgroundColor: mode === 'dark' ? 'rgba(255, 255, 255, 0.08)' : 'background.paper',
                          position: 'sticky',
                          left: 0,
                          zIndex: 1,
                          borderRight: `1px solid ${mode === 'dark' ? 'rgba(255, 255, 255, 0.12)' : 'rgba(224, 224, 224, 1)'}`,
                          py: 0.25,
                          px: 1,
                        }}
                        onClick={(e) => e.stopPropagation()}
                      >
                        <Checkbox
                          checked={isChecked}
                          onChange={() => handleDriverCheckboxChange(row.Driver)}
                          size="small"
                        />
                      </TableCell>
                      <TableCell
                        sx={{
                          backgroundColor: mode === 'dark' ? 'rgba(255, 255, 255, 0.08)' : 'background.paper',
                          position: 'sticky',
                          left: '60px',
                          zIndex: 1,
                          borderRight: `1px solid ${mode === 'dark' ? 'rgba(255, 255, 255, 0.12)' : 'rgba(224, 224, 224, 1)'}`,
                          fontWeight: 'medium',
                          py: 0.25,
                          px: 1,
                        }}
                      >
                        {row.TrackRank != null && row.TrackRank !== undefined
                          ? Math.round(Number(row.TrackRank))
                          : rowIndex + 1}
                      </TableCell>
                      {visibleColumnsTrack.map((column) => {
                        const value = row[column.id as keyof TrackDriverStatRow];
                        const statId = column.id as string;
                        return (
                          <TableCell
                            key={column.id}
                            align={column.id === 'Driver' ? 'left' : 'right'}
                            sx={{
                              backgroundColor: column.id === 'Driver' ? undefined : getCellColor(Number(value), statId),
                              color: column.id !== 'Driver' ? (mode === 'dark' ? '#000000' : '#000000') : undefined,
                              fontWeight: 'medium',
                              transition: 'all 0.2s',
                              py: 0.25,
                              px: 1,
                            }}
                          >
                            {column.id === 'Driver' ? value :
                             column.id === 'PctQPass' || column.id === 'PctT15Lap' || column.id === 'PctLed' 
                              ? `${Number(value).toFixed(1)}%` :
                             column.id === 'DriverRating' || column.id === 'Avg' || column.id === 'Pos' || column.id === 'St'
                              ? Number(value).toFixed(1) :
                             Math.round(Number(value))}
                          </TableCell>
                        );
                      })}
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </DndContext>
      </>
      )}

      <DriverDetailsModal
        open={isDetailsModalOpen}
        onClose={() => setIsDetailsModalOpen(false)}
        driversDetails={driverDetails}
        isLoading={isDetailsLoading}
      />
    </Box>
  );
};

export default TrackRankings; 